import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatMoney } from "../lib/format";
import { useT } from "../i18n";

interface ReceiptData {
  receiptNo: string;
  orgName: string;
  hallName: string;
  customerName: string;
  customerPhone: string;
  eventDateJalali: string;
  amountMinor: number;
  currency: string;
  method: string;
  purpose: string;
  status: string;
  bookingTotalMinor: number;
  paidMinor: number;
  balanceMinor: number;
  issuedJalali: string;
  note: string | null;
}

/**
 * Printable payment/deposit receipt (رسید). Only the receipt card carries the
 * `receipt-print` class, and an injected @media print rule hides everything
 * else — so browser print (→ PDF or paper) yields a clean one-page receipt.
 */
export function PrintableReceipt({
  paymentId,
  onClose,
}: {
  paymentId: string | null;
  onClose: () => void;
}) {
  const { orgId } = useSession();
  const [data, setData] = useState<ReceiptData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  useEffect(() => {
    if (!paymentId || !orgId) return;
    setData(null);
    setError(null);
    api<ReceiptData>(`/orgs/${orgId}/payments/${paymentId}/receipt`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : t("common_error")));
  }, [paymentId, orgId, t]);

  if (!paymentId) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <style>{`@media print {
        body * { visibility: hidden !important; }
        .receipt-print, .receipt-print * { visibility: visible !important; }
        .receipt-print { position: fixed; inset: 0; margin: 24px; }
        .receipt-noprint { display: none !important; }
      }`}</style>
      <DialogTitle className="receipt-noprint">
        <Stack direction="row" alignItems="center">
          <Typography variant="h6" sx={{ flex: 1 }}>
            {t("receipt_print_title")}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label={t("common_close")}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!data && !error && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}
        {data && (
          <Box
            className="receipt-print"
            sx={{ border: "1px solid #ddd", borderRadius: 2, p: 3, bgcolor: "#fff" }}
          >
            <Stack alignItems="center" spacing={0.5} mb={2}>
              <Typography variant="h6" fontWeight={800} color="primary">
                {data.orgName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("receipt_print_subtitle", { purpose: data.purpose })}
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            <ReceiptRow label={t("receipt_print_no")} value={data.receiptNo} />
            <ReceiptRow label={t("receipt_print_issued")} value={data.issuedJalali} />
            <ReceiptRow label={t("receipt_print_hall")} value={data.hallName} />
            <ReceiptRow label={t("receipt_print_customer")} value={data.customerName} />
            <ReceiptRow label={t("receipt_print_phone")} value={data.customerPhone} />
            <ReceiptRow label={t("receipt_print_event_date")} value={data.eventDateJalali} />
            <ReceiptRow label={t("receipt_print_method")} value={data.method} />
            <ReceiptRow label={t("common_status")} value={data.status} />

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={700}>{t("receipt_print_amount")}</Typography>
              <Typography variant="h6" fontWeight={800} color="primary">
                {formatMoney(data.amountMinor, data.currency)}
              </Typography>
            </Stack>
            <Divider sx={{ my: 1.5 }} />

            <ReceiptRow label={t("receipt_print_contract_total")} value={formatMoney(data.bookingTotalMinor)} />
            <ReceiptRow label={t("receipt_print_total_paid")} value={formatMoney(data.paidMinor)} />
            <ReceiptRow label={t("receipt_print_balance")} value={formatMoney(data.balanceMinor)} strong />
            {data.note && <ReceiptRow label={t("receipt_print_note")} value={data.note} />}

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" justifyContent="space-between" mt={3}>
              <Typography variant="caption" color="text.secondary">
                {t("receipt_print_hall_sign")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("receipt_print_customer_sign")}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              mt={2}
            >
              {t("receipt_print_footer")}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions className="receipt-noprint">
        <Button onClick={onClose}>{t("common_close")}</Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          disabled={!data}
        >
          {t("contract_print_pdf")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReceiptRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" py={0.5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={strong ? 700 : 500}>
        {value}
      </Typography>
    </Stack>
  );
}
